import React, { useState, useEffect } from 'react';
import { StageProps } from '../WizardContainer';
import { profileService } from '../../../services/api';
import { DatingProfile, GENDER_OPTIONS, RELATIONSHIP_OPTIONS, RelationshipType } from '../../../types/profile';

interface Stage1Props extends StageProps {
  step: number;
}

/** Age from a yyyy-mm-dd birthdate string; -1 when unparsable. */
function ageFromBirthdate(birthdate: string): number {
  const dob = new Date(birthdate);
  if (isNaN(dob.getTime())) return -1;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

const Stage1Essentials: React.FC<Stage1Props> = ({
  onNext,
  onBack,
  isLoading,
  step,
}) => {
  const [profile, setProfile] = useState<Partial<DatingProfile>>({});
  const [birthdate, setBirthdate] = useState('');
  // Whether a dating profile row already exists server-side. A fresh signup
  // has none — the stage must POST create (with birthdate) instead of PUT.
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadProfile = async () => {
      const existing = await profileService.getProfile();
      if (existing) {
        setProfile(existing);
        setHasExistingProfile(true);
      }
    };
    loadProfile();
  }, []);

  const updateField = (field: keyof DatingProfile, value: unknown) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    if (!profile.displayName?.trim()) {
      newErrors.displayName = 'Display name is required';
    }
    if (!hasExistingProfile) {
      if (!birthdate) {
        newErrors.birthdate = 'Date of birth is required';
      } else {
        const age = ageFromBirthdate(birthdate);
        if (age < 0) {
          newErrors.birthdate = 'Enter a valid date of birth';
        } else if (age < 18) {
          newErrors.birthdate = 'You must be 18 or older';
        } else if (age > 120) {
          newErrors.birthdate = 'Enter a valid date of birth';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};
    if (!profile.gender) {
      newErrors.gender = 'Please select your gender';
    }
    if (!profile.lookingFor) {
      newErrors.lookingFor = 'Please select what you are looking for';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors: Record<string, string> = {};
    if (!profile.locationCity?.trim()) {
      newErrors.locationCity = 'City is required';
    }
    if (!profile.locationCountry?.trim()) {
      newErrors.locationCountry = 'Country is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    let isValid = false;
    switch (step) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      default:
        isValid = true;
    }

    if (!isValid) return;

    // Steps 1–2 only collect locally; the server write happens once at the
    // end of the stage. A fresh signup has no dating-profile row yet, and
    // PUT /api/profile 404s without one — so step 3 creates it (POST needs
    // the birthdate) and then PUTs the fields the create call doesn't accept.
    if (step < 3) {
      onNext(profile as Record<string, unknown>);
      return;
    }

    setSaving(true);
    try {
      if (!hasExistingProfile) {
        await profileService.createProfile({
          displayName: profile.displayName!,
          birthdate,
          gender: profile.gender!,
          lookingFor: profile.lookingFor || 'not_sure',
          locationCity: profile.locationCity,
        });
        setHasExistingProfile(true);
        if (profile.locationRegion || profile.locationCountry) {
          await profileService.updateProfile({
            locationRegion: profile.locationRegion,
            locationCountry: profile.locationCountry,
          });
        }
      } else {
        await profileService.updateProfile({
          displayName: profile.displayName,
          locationCity: profile.locationCity,
          locationRegion: profile.locationRegion,
          locationCountry: profile.locationCountry,
          lookingFor: profile.lookingFor,
        });
      }
      onNext(profile as Record<string, unknown>);
    } catch (e) {
      console.error('[Wizard] Failed to save essentials:', e);
      setErrors({ locationCountry: 'Could not save your profile — please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const renderStep1 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Let's start with the basics</h3>

      <div className="wizard-form-group">
        <label htmlFor="displayName">Display Name</label>
        <input
          type="text"
          id="displayName"
          value={profile.displayName || ''}
          onChange={(e) => updateField('displayName', e.target.value)}
          placeholder="How should we call you?"
          className={errors.displayName ? 'error' : ''}
          maxLength={50}
        />
        {errors.displayName && (
          <span className="wizard-error">{errors.displayName}</span>
        )}
        <span className="wizard-hint">This is what others will see</span>
      </div>

      {hasExistingProfile ? (
        <div className="wizard-form-group">
          <label>Age</label>
          <input type="number" value={profile.age || ''} disabled readOnly />
          <span className="wizard-hint">Age comes from your date of birth and can't be changed here</span>
        </div>
      ) : (
        <div className="wizard-form-group">
          <label htmlFor="birthdate">Date of Birth</label>
          <input
            type="date"
            id="birthdate"
            value={birthdate}
            onChange={(e) => {
              setBirthdate(e.target.value);
              setErrors((prev) => ({ ...prev, birthdate: '' }));
            }}
            className={errors.birthdate ? 'error' : ''}
          />
          {errors.birthdate && (
            <span className="wizard-error">{errors.birthdate}</span>
          )}
          <span className="wizard-hint">You must be 18 or older — only your age is shown to others</span>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">About you</h3>

      <div className="wizard-form-group">
        <label>I am a</label>
        <div className="wizard-options-grid">
          {GENDER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.gender === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('gender', option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {errors.gender && <span className="wizard-error">{errors.gender}</span>}
      </div>

      <div className="wizard-form-group">
        <label>Looking for</label>
        <div className="wizard-options-grid">
          {RELATIONSHIP_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`wizard-option-btn ${
                profile.lookingFor === option.value ? 'selected' : ''
              }`}
              onClick={() => updateField('lookingFor', option.value as RelationshipType)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {errors.lookingFor && (
          <span className="wizard-error">{errors.lookingFor}</span>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step">
      <h3 className="wizard-step-title">Where are you located?</h3>

      <div className="wizard-form-group">
        <label htmlFor="locationCity">City</label>
        <input
          type="text"
          id="locationCity"
          value={profile.locationCity || ''}
          onChange={(e) => updateField('locationCity', e.target.value)}
          placeholder="Your city"
          className={errors.locationCity ? 'error' : ''}
        />
        {errors.locationCity && <span className="wizard-error">{errors.locationCity}</span>}
      </div>

      <div className="wizard-form-group">
        <label htmlFor="locationCountry">Country</label>
        <input
          type="text"
          id="locationCountry"
          value={profile.locationCountry || ''}
          onChange={(e) => updateField('locationCountry', e.target.value)}
          placeholder="Your country"
          className={errors.locationCountry ? 'error' : ''}
        />
        {errors.locationCountry && <span className="wizard-error">{errors.locationCountry}</span>}
      </div>

      <div className="wizard-form-group">
        <label htmlFor="locationRegion">State/Province (optional)</label>
        <input
          type="text"
          id="locationRegion"
          value={profile.locationRegion || ''}
          onChange={(e) => updateField('locationRegion', e.target.value)}
          placeholder="Your state or province"
        />
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="wizard-stage">
      {renderCurrentStep()}

      <div className="wizard-stage-actions">
        {step > 1 && (
          <button
            type="button"
            className="wizard-btn wizard-btn-secondary"
            onClick={onBack}
            disabled={isLoading}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="wizard-btn wizard-btn-primary"
          onClick={handleNext}
          disabled={isLoading || saving}
        >
          {isLoading || saving ? 'Saving...' : step === 3 ? 'Complete Stage' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Stage1Essentials;
